import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetResultBlobs,
  GetResultBlobsVariables,
} from 'insights/analytics-visualizations/blobs/__generated/GetResultBlobs';
import {BLOB_QUERY} from 'insights/analytics-visualizations/blobs/QueryingBlobLinkList';
import * as BlobActions from 'schemas/tags/BlobActions';
import BlobInputModal from 'schemas/tags/BlobInputModal';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ModalAnchor from 'ui-library/ModalAnchor';

import LabelWrapper from '../shared/LabelWrapper';
import * as uiBlock from '../UiBlockTypes';

type ResultBlobGQL = ExcludeInaccessible<GetResultBlobs['resultBlobs'][number]>;

interface ResolvedFileUploadUiBlockProps {
  blobs: Array<ResultBlobGQL>;
  onChangeValue: (blockId: string, val: Array<string>) => void;
  id: string;
  enabled: boolean;
  canvasEnabled: boolean;
  label: string | null;
  fileTypes: Array<string> | null;
  maxFiles: number;
}

function ResolvedFileUploadUiBlock({
  blobs,
  onChangeValue,
  id,
  enabled,
  canvasEnabled,
  label,
  fileTypes,
  maxFiles,
}: ResolvedFileUploadUiBlockProps): JSX.Element {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  function handleChangeBlobs(blobIds: Array<string>): void {
    onChangeValue(id, blobIds);
  }
  function handleAddBlobs(newBlobIds: Array<string>): void {
    handleChangeBlobs([...blobs.map((blob) => blob.id), ...newBlobIds]);
  }
  function removeBlobAtIndex(index: number): void {
    const newBlobs = reactUpdate(blobs, {$splice: [[index, 1]]});
    handleChangeBlobs(newBlobs.map((blob) => blob.id));
  }
  const isEnabled = canvasEnabled && enabled;
  const numAdditionalBlobsAllowed = maxFiles - blobs.length;
  const canAddMoreBlobs = isEnabled && numAdditionalBlobsAllowed > 0;
  const allowMultiple = numAdditionalBlobsAllowed > 1;
  const uploadFileButtonTooltip = !isEnabled
    ? 'Read-only'
    : numAdditionalBlobsAllowed === 0
      ? `You can't upload more than ${maxFiles} ${pluralize(maxFiles, 'file')}.`
      : '';
  return (
    <div>
      <LabelWrapper label={label} />
      <TooltipWrapper tooltipTitle={uploadFileButtonTooltip}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          className={css(styles.fileUploadButton)}
          text={`Upload ${pluralize(1 + (maxFiles - 1) * (numAdditionalBlobsAllowed - 1), 'file')}`}
          icon="upload"
          disabled={!canAddMoreBlobs}
          textClassName="button-text--nowrap"
          onClick={() => {
            setIsModalOpen(true);
          }}
        />
      </TooltipWrapper>
      <div className={css(styles.filesContainer)}>
        {blobs.map((blob, index) => (
          <Chip
            key={index}
            className={classNames('chip-container', css(styles.fileUploadChip))}
            icon="paperclip"
            size={ChipSize.MINI}
            text={blob.name}
            href={blob.url}
            {...(isEnabled && {
              onRemove: (e) => {
                removeBlobAtIndex(index);
              },
            })}
            faded={!isEnabled}
          />
        ))}
      </div>
      {isModalOpen && (
        <ModalAnchor open={true}>
          <BlobInputModal
            allowMultiple={allowMultiple}
            onRequestClose={() => {
              setIsModalOpen(false);
            }}
            onUploadFiles={(files) =>
              BlobActions.uploadFilesAsBlobs(files, {
                shouldGeneratePreviewFile: true,
                onBlobsCreated: (newBlobs) => {
                  handleAddBlobs(newBlobs.map((blob) => blob.id));
                },
              })
            }
            fileTypes={fileTypes || undefined}
            {...(allowMultiple && {maxFiles: numAdditionalBlobsAllowed})}
          />
        </ModalAnchor>
      )}
    </div>
  );
}

interface FileUploadUiBlockProps extends uiBlock.FileUpload {
  onChangeValue: (blockId: string, val: Array<string>) => void;
}

function FileUploadUiBlock({
  blockValue,
  onChangeValue,
  id,
  enabled,
  canvasEnabled,
  label,
  fileTypes,
  maxFiles,
}: FileUploadUiBlockProps): JSX.Element {
  return (
    <BenchlingQuery<GetResultBlobs, GetResultBlobsVariables>
      query={BLOB_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{ids: blockValue?.values || []}}
    >
      {(data) =>
        data.resultBlobs.some((blob) => isInaccessible(blob)) ? (
          <NoAccessChip size={ChipSize.SMALL} />
        ) : (
          <ResolvedFileUploadUiBlock
            id={id}
            onChangeValue={onChangeValue}
            enabled={enabled}
            canvasEnabled={canvasEnabled}
            label={label}
            fileTypes={fileTypes}
            maxFiles={maxFiles}
            blobs={data.resultBlobs as Array<ResultBlobGQL>}
          />
        )
      }
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  filesContainer: {
    // display: 'flex-wrap',
    alignItems: 'center',
    gap: '8px',
  },
  fileUploadButton: {
    display: 'block',
    marginBottom: '8px',
  },
  fileUploadChip: {
    marginBottom: '4px',
    marginRight: '4px',
  },
});

export default FileUploadUiBlock;
